// Import necessary modules and hooks from React
import { useEffect, useState, useMemo } from "react";
import styled from "styled-components";
import { DeckGLHeatmap } from "./DeckGLHeatmap";
import SliderPicker from "../../Atomics/Slider";
import Spinner from "../../Atomics/Spinner";
import useFetch from "../../../../server/useFetch";
import query from "../../../../server/constants.api";
import { REACT_APP_MAPS_API_KEY } from "../../../../utils/config/envConfig";

// Define the HeatmapComponent function component
export const HeatmapComponent = ({ idQuery, mapId, tenant, coordinates }) => {
    // Define months array
    const months = ["jan","feb","mar","apr","may","jun","jul","aug","sep","oct","nov","dec"];
    const actualDate = new Date();

    // Define states for month, map items, and configurations
    const [month, setMonth] = useState(months[actualDate.getMonth()]);
    const [year, setYear] = useState(actualDate.getFullYear());
    const [mapItems, setMapItems] = useState(null);
    const [configurations, setConfigurations] = useState(null);
    const [googleReady, setGoogleReady] = useState(!!window.google?.maps);

    // Load Google Maps script if not already loaded
    useEffect(() => {
        if (window.google?.maps) {
            setGoogleReady(true);
            return;
        }
        const script = document.createElement("script");
        script.src = `https://maps.googleapis.com/maps/api/js?key=${REACT_APP_MAPS_API_KEY}`;
        script.async = true;
        script.defer = true;
        script.onload = () => setGoogleReady(true);
        script.onerror = () => console.error("Failed to load Google Maps script");
        document.head.appendChild(script);
    }, []);

    // Fetch map configurations and data using custom hook useFetch
    const { data: mapConfigurationsData, refetch: refetchMapConfigurationsData, isFetching: mapConfigurationIsFetching } = useFetch(query.path.mapConfiguration, 'heatmapConfigurations' + idQuery, { id: 'mapPTDs', lat: Number(coordinates.latitude), lng: Number(coordinates.longitude) });
    const { data: mapData, refetch: refetchMapData, isFetching: mapDataIsFetching } = useFetch(query.path.territoryHeatmap, 'heatmap' + idQuery, { tenant: tenant, month: month, year: year });

    // Effect to handle map configurations data
    useEffect(() => {
        try {
            if (mapConfigurationsData?.data) {
                setConfigurations(mapConfigurationsData?.data);
            }
        } catch (error) {
            console.error('(HeatmapComponent page) Error in useEffect handling map configurations:', error);
        }
    }, [mapConfigurationIsFetching]);

    // Effect to handle map data — transform to deck.gl format { latitude, longitude, weight }
    useEffect(() => {
        try {
            if (mapData?.data) {
                const points = mapData.data
                    .filter(x => x.latitude && x.longitude)
                    .map(x => ({
                        latitude: Number(x.latitude),
                        longitude: Number(x.longitude),
                        weight: Number(x.Value) * 1000000,
                    }));
                setMapItems(points);
            }
        } catch (error) {
            console.error('(HeatmapComponent page) Error in useEffect setting map items:', error);
        }
    }, [mapData]);

    // Compute the initial view state from configurations or coordinates
    const initialViewState = useMemo(() => ({
        latitude: configurations?.center?.lat ?? configurations?.lat ?? Number(coordinates.latitude) ?? 41.15,
        longitude: configurations?.center?.lng ?? configurations?.lng ?? Number(coordinates.longitude) ?? -8.61,
        zoom: configurations?.zoom ?? 11,
        pitch: 0,
        bearing: 0,
    }), [configurations, coordinates]);

    // Effect to refetch map data when month changes
    useEffect(() => {
        refetchMapData();
    }, [month]);

    // Effect to refetch map configurations and map data when coordinates change
    useEffect(() => {
        refetchMapConfigurationsData();
        refetchMapData();
    }, [coordinates]);

    // Function to handle month change
    function onChangePicker(month, year) {
        setYear(year);
        setMonth(month);
    }

    const isReady = googleReady && mapItems && !mapDataIsFetching && !mapConfigurationIsFetching;

    // Render HeatmapComponent
    return (
        <MapDiv>
            {isReady ? (
                <div className="mapWrapper">
                    <DeckGLHeatmap
                        data={mapItems}
                        initialViewState={initialViewState}
                        apiKey={REACT_APP_MAPS_API_KEY}
                        intensity={1}
                        radiusPixels={30}
                        threshold={0.03}
                        id={mapId || "territory-heatmap"}
                        mapId={'e1816747e3cd86e6'}
                    />
                </div>
            ) : (
                <div className="map">
                    <Spinner stroke="green-4" />
                </div>
            )}
            {/* Render SliderPicker */}
            <SliderPicker value={month} onChangePicker={onChangePicker} />
        </MapDiv>
    );
};

//#region  Styled Components
const MapDiv = styled.div`
    height: 100%;
    
    .mapWrapper {
        height: 85%;
        padding: 0;
        width: -webkit-fill-available;
    }

    .map {
        height: 85%;
        display: flex;
        align-items: center;
        justify-content: center;
    }
`;
//#endregion